import { tokens } from '@/design';
import type { CSSProperties } from 'react';
import type { LayoutPictureElement } from './layoutTypes';
import { ptToMm } from './pageUnits';

export interface LayoutPictureGraphicProps {
  readonly element: LayoutPictureElement;
}

export function LayoutPictureGraphic({ element }: LayoutPictureGraphicProps) {
  if (!element.visible) return null;
  const background = element.background;
  const border = element.border;
  const shadow = element.shadow;
  const frame: CSSProperties = {
    position: 'absolute',
    inset: 0,
    transform: `rotate(${element.rotation}deg) scale(${element.flipX ? -1 : 1}, ${element.flipY ? -1 : 1})`,
    transformOrigin: 'center',
    opacity: element.opacityPercent / 100,
  };
  const gapX = Math.max(background.gapXPt, border.gapXPt);
  const gapY = Math.max(background.gapYPt, border.gapYPt);
  return (
    <div data-testid="layout-picture" data-picture-name={element.name} style={frame}>
      {shadow.enabled ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            transform: `translate(${ptToMm(shadow.offsetXPt)}mm, ${ptToMm(shadow.offsetYPt)}mm)`,
            background: shadow.color,
            zIndex: 0,
          }}
        />
      ) : null}
      {background.enabled ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: background.color,
            zIndex: 1,
          }}
        />
      ) : null}
      <img
        alt=""
        src={element.source.dataUrl}
        draggable={false}
        style={{
          position: 'absolute',
          left: `${ptToMm(gapX)}mm`,
          top: `${ptToMm(gapY)}mm`,
          right: `${ptToMm(gapX)}mm`,
          bottom: `${ptToMm(gapY)}mm`,
          width: `calc(100% - ${ptToMm(gapX * 2)}mm)`,
          height: `calc(100% - ${ptToMm(gapY * 2)}mm)`,
          objectFit: 'fill',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />
      {border.enabled && border.outlineWidthPt > 0 ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            border: `${border.outlineWidthPt}pt solid ${border.outlineColor || tokens.color.ink.inverse}`,
            background: 'transparent',
            zIndex: 3,
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </div>
  );
}
